import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Button, Flex, Skeleton, Switch, useToast } from '@chakra-ui/react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { breadcrumb } from './breadcrumb';
import { PredictionsFilters } from './types';
import { Predictions, PredictionsInteractivity } from '@/model';
import { PredictionsResult } from '@/model/predictions';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useAppApplicationComponentArea } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { PredictionsFiltersBox } from './components/PredictionsFiltersBox';
import { PredictionsTable } from './components/PredictionsTable';
import { queryClient } from '@/services/api/queryClient';
import {
  useDeletePredictionsMutation,
  useGetPredictions,
  useGetPredictionsByUUID,
  useGetPredictionsRanking,
  useGetPredictionsResult,
  usePutPredictionsInteractiveMutation,
} from '@/services/queries/predictions';
import { PredictionsCreateModal } from './components/PredictionsCreateModal';
import { PredictionsEditModal } from './components/PredictionsEditModal';
import { PredictionsResultModal } from './components/PredictionsResultModal';
import { PredictionsResultDetailModal } from './components/PredictionsResultDetailModal';
import { PredictionsRankingModal } from './components/PredictionsRankingModal';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationPredictions = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.PREDICTIONS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResultModalOpen, setResultModalOpen] = useState(false);
  const [isResultDetailModalOpen, setResultDetailModalOpen] = useState(false);
  const [resultDetailItem, setResultDetailItem] = useState<PredictionsResult | undefined>(undefined);
  const [isRankingModalOpen, setRankingModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Predictions | undefined>(undefined);

  const deleteMutation = useDeletePredictionsMutation();
  const getItemByUUID = useGetPredictionsByUUID();
  const getPredictionsResult = useGetPredictionsResult();
  const getPredictionsRanking = useGetPredictionsRanking();
  const interactiveMutation = usePutPredictionsInteractiveMutation();

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_predictions.application_predictions_enable_success')
      : t('pages.application_predictions.application_predictions_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_predictions.application_predictions_enable_error')
      : t('pages.application_predictions.application_predictions_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.PREDICTIONS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const filtersForm = useFiltersForm<PredictionsFilters>({
    defaultValues: {
      team_uuid: '',
      value: '',
      status: '',
      dateValue: DateFilterInputValue.ALL,
      start_date: '',
      end_date: '',
      interactivity: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useGetPredictions(params);

  const itemsData = itemData?.data || [];

  const { data: clubs = [] } = useAllClub(true);

  const onHandleDelete = (item: Predictions) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const cancelResultModal = () => {
    setResultModalOpen(false);
    setSelectedItem(undefined);
  };

  const onHandleViewResult = (item: Predictions) => {
    setResultDetailModalOpen(true);
    setResultDetailItem(undefined);

    getPredictionsResult.mutate(item.uuid, {
      onSuccess: res => {
        setResultDetailItem(res);
      },
      onError: (resp: any) => {
        setResultDetailModalOpen(false);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_predictions.application_predictions_result_error'),
        });
      },
    });
  };

  const cancelResultDetailModal = () => {
    setResultDetailModalOpen(false);
    setResultDetailItem(undefined);
  };

  const onHandleViewRanking = () => {
    setRankingModalOpen(true);

    getPredictionsRanking.mutate(undefined, {
      onError: (resp: any) => {
        setRankingModalOpen(false);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_predictions.application_predictions_ranking_error'),
        });
      },
    });
  };

  const handleFinish = () => {
    if (!selectedItem?.uuid) return;

    const payload = {
      interactivity: PredictionsInteractivity.CLOSED,
    };
    interactiveMutation.mutate(
      {
        id: selectedItem?.uuid,
        payload,
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_predictions.application_predictions_finish_success'),
            status: 'success',
          });

          queryClient.invalidateQueries(['GET_PREDICTIONS', params]);
          setFinishModalOpen(false);
          setSelectedItem(undefined);
        },
        onError: (e: any) => {
          setFinishModalOpen(false);
          setSelectedItem(undefined);
          toast({
            title: e?.message || t('pages.application_predictions.application_predictions_finish_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_predictions.application_predictions_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_predictions.application_predictions_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_predictions.application_predictions_not_found_error'),
        });
      },
    });
  };

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent
      title={t('pages.application_predictions.application_predictions_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
          <Button
            onClick={onHandleViewRanking}
            variant='outlined'
            display='flex'
            alignItems='center'
            gap={2}
            width={{ base: '100%', md: 'initial' }}
          >
            {t('pages.application_predictions.application_predictions_ranking_button')}
          </Button>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            display='flex'
            alignItems='center'
            gap={2}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_predictions.application_predictions_create_button')}
          </Button>
        </Flex>
      }
    >
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_predictions.application_predictions_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_predictions.application_predictions_enabled')
                    : t('pages.application_predictions.application_predictions_disabled')}
                </Switch>
              </>
            )}
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <PredictionsFiltersBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <PredictionsTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onFinishClick={item => {
              setSelectedItem(item);
              setFinishModalOpen(true);
            }}
            onInsertResultClick={item => {
              setSelectedItem(item);
              setResultModalOpen(true);
            }}
            onViewResultClick={onHandleViewResult}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && isEditModalOpen ? (
        <PredictionsEditModal
          item={selectedItem}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onDone={() => {
            setIsEditModalOpen(false);
            queryClient.invalidateQueries(['GET_PREDICTIONS', params]);
          }}
        />
      ) : null}

      <PredictionsCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={finishModalOpen}
        withoutCloseButton
        onClose={() => {
          setFinishModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={handleFinish}
        title={t('pages.application_predictions.application_predictions_finish_modal_title')}
        description={t('pages.application_predictions.application_predictions_finish_modal_description')}
        isLoading={deleteMutation.isLoading}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_predictions.application_predictions_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_predictions.application_predictions_delete_modal_description')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.identifier}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_predictions.application_predictions_toggle_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_predictions.application_predictions_toggle_modal_enable_description')
              : t('pages.application_predictions.application_predictions_toggle_modal_disable_description')}
          </Text>
        }
      />

      {selectedItem &&
      selectedItem.interactivity === PredictionsInteractivity.CLOSED &&
      !selectedItem.option_a_result &&
      !selectedItem.option_b_result ? (
        <PredictionsResultModal
          isOpen={isResultModalOpen}
          onClose={cancelResultModal}
          onDone={cancelResultModal}
          item={selectedItem}
        />
      ) : null}

      <PredictionsResultDetailModal
        isOpen={isResultDetailModalOpen}
        isLoading={getPredictionsResult.isLoading}
        item={resultDetailItem}
        onClose={cancelResultDetailModal}
      />

      <PredictionsRankingModal
        isOpen={isRankingModalOpen}
        isLoading={getPredictionsRanking.isLoading}
        items={getPredictionsRanking.data ?? []}
        onClose={() => setRankingModalOpen(false)}
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
